import type {
  HeroDefinition,
  TypedValidatorResult,
  ValidatorIssue,
} from "../../../schemas"
import { HERO_LEVEL_UP_COSTS } from "../../../schemas"
import { SKILL_MAP } from "../content/skills"

const MAX_SKILLS_PER_LEVEL = 3
const HERO_LEVEL_TARGET_COSTS: Record<number, number> = { 1: 2, 2: 5, 3: 10 }

export function validateHero(
  hero: HeroDefinition,
): TypedValidatorResult<HeroDefinition> {
  const issues: ValidatorIssue[] = []

  // Progression must have exactly 3 levels in order
  const levels = hero.progression.map((l) => l.level)
  if (levels[0] !== 1 || levels[1] !== 2 || levels[2] !== 3) {
    issues.push({
      code: "HERO_INVALID_PROGRESSION_ORDER",
      message: "Hero progression must have levels 1, 2, and 3 in order.",
      path: "progression",
      severity: "error",
    })
  }

  for (const levelDef of hero.progression) {
    const levelIssues: ValidatorIssue[] = []
    const prefix = `progression[${levelDef.level - 1}]`

    // Skill count per level
    if (levelDef.card.selectedSkills.length > MAX_SKILLS_PER_LEVEL) {
      levelIssues.push({
        code: "HERO_TOO_MANY_SKILLS",
        message: `Hero level ${levelDef.level} may have at most ${MAX_SKILLS_PER_LEVEL} skills.`,
        path: `${prefix}.card.selectedSkills`,
        severity: "error",
      })
    }

    // Skill affinity
    for (const sel of levelDef.card.selectedSkills) {
      const def = SKILL_MAP.get(sel.skillId)
      if (!def) {
        levelIssues.push({
          code: "HERO_UNKNOWN_SKILL",
          message: `Unknown skill "${sel.skillId}" on level ${levelDef.level}.`,
          path: `${prefix}.card.selectedSkills`,
          severity: "error",
        })
        continue
      }

      const affinityOk =
        def.isColorless ||
        def.affinity === hero.primaryColor ||
        (hero.secondaryColor && def.affinity === hero.secondaryColor)
      if (!affinityOk) {
        levelIssues.push({
          code: "HERO_SKILL_AFFINITY_MISMATCH",
          message: `Skill "${sel.skillId}" (affinity: ${def.affinity}) is not available to a ${hero.primaryColor}${hero.secondaryColor ? `/${hero.secondaryColor}` : ""} hero.`,
          path: `${prefix}.card.selectedSkills`,
          severity: "error",
        })
      }
    }

    // Target cost advisory (warning only — heroes are priced separately)
    const expected = HERO_LEVEL_TARGET_COSTS[levelDef.level]
    if (expected !== undefined && levelDef.targetCost !== expected) {
      levelIssues.push({
        code: "HERO_TARGET_COST_DEVIATION",
        message: `Level ${levelDef.level} target cost is ${levelDef.targetCost}; standard is ${expected}.`,
        path: `${prefix}.targetCost`,
        severity: "warning",
      })
    }

    issues.push(...levelIssues)
  }

  // Expose level-up costs for informational reference (not a validation error)
  void HERO_LEVEL_UP_COSTS

  // Secondary color must differ from primary
  if (hero.secondaryColor && hero.secondaryColor === hero.primaryColor) {
    issues.push({
      code: "HERO_DUPLICATE_COLOR",
      message: "Hero secondary color must differ from primary color.",
      path: "secondaryColor",
      severity: "error",
    })
  }

  const valid = issues.every((i) => i.severity !== "error")
  return { valid, value: valid ? hero : undefined, issues }
}
